import { useMemo } from "react";
import {
  FilterType,
  FilterValue,
  InputType,
  SelectFilter,
  StringFilterValue,
} from "@coworker/apprestructured/src/shared/filters/types/filter";
import { ApplyFilter } from "@coworker/apprestructured/src/shared/filters/types/apply";
import { Article, Combined } from "../types/article";
import { useCustomAllSelectOption } from "@coworker/apprestructured/src/shared/filters/utils/useAllSelectOption";
import { useTranslation } from "@coworker/locales";
import { ALERT_ID } from "./useAlertSelectArticleFilter";

export const EDS_DATES_ID = "edsDates";
const NOW = Date.now();

export function useEdsDateOptions(edsDates: string[]) {
  return useMemo(
    () =>
      edsDates
        .sort()
        .reverse()
        .map((edsDate) => {
          const title = edsDate.split("T")[0] ?? edsDate;
          return { value: edsDate, title, dependsOnFilterId: ALERT_ID };
        }),
    [edsDates]
  );
}

function useEdsDatesSelectFilter(
  filterValues: FilterValue[],
  edsDates: string[]
) {
  const { t } = useTranslation();
  const defaultValue = { filterId: EDS_DATES_ID, value: "" };

  const edsDateSelectOptions = useEdsDateOptions(edsDates);

  const alertsValue = filterValues.find((f) => f.filterId === ALERT_ID) as
    StringFilterValue | undefined;

  const now = NOW;

  const isEdsType = alertsValue?.value === "eds";
  const isPastEdsType = alertsValue?.value === "pastEds";

  const enabled = isEdsType || isPastEdsType;

  const titleKey = isPastEdsType ? "pedsDatesString" : "edsDatesString";

  const allLabelKey = isPastEdsType
    ? "allPedsDatesString"
    : "allEdsDatesString";

  const options = !enabled
    ? []
    : isPastEdsType
      ? edsDateSelectOptions.filter(
          ({ value }) => new Date(value).getTime() < now
        )
      : edsDateSelectOptions.filter(
          ({ value }) => new Date(value).getTime() >= now
        );

  return useCustomAllSelectOption(
    {
      inputType: InputType.Select,
      filterType: FilterType.Filter,
      id: EDS_DATES_ID,
      title: t(titleKey),
      options,
      enabled,
      defaultValue,
    },
    t(allLabelKey)
  );
}

function useEdsDatesSelectArticleFilter(
  filterValues: FilterValue[],
  edsDates: string[]
) {
  const filter = useEdsDatesSelectFilter(filterValues, edsDates);

  const applyFilter: ApplyFilter<SelectFilter, Article> = {
    filter,
    filterCompare: (item: Combined<Article>, { value }: FilterValue) => {
      if (filter.enabled && value) {
        if (!item.eds) {
          return false;
        }
        return value === item.eds.validToDateTime;
      } else {
        return true;
      }
    },
  };

  return applyFilter;
}

export { useEdsDatesSelectArticleFilter };
